import { Link } from 'react-router-dom'
import { Sprout, Droplet, Sun, Users, ShoppingBag, Heart, User } from 'lucide-react'
import { useEffect, useState } from 'react'
import Button from '../components/Button'
import { getCommunityPlants } from '../lib/supabase'
import PlantPreview from '../components/PlantPreview'
import { useI18n } from '../i18n/I18nProvider'

function LandingPage() {
  const [topPlants, setTopPlants] = useState([])
  const [loading, setLoading] = useState(true)
  const { t } = useI18n()

  useEffect(() => {
    const fetchTopPlants = async () => {
      try {
        const plants = await getCommunityPlants(6)
        setTopPlants(plants)
      } catch (error) {
        console.error('Erreur lors du chargement des plantes:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchTopPlants()
  }, [])

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold text-green-800 mb-6 pixel-font">
            {t('landing.heroTitle')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-8">
            {t('landing.heroSubtitle')}
          </p>
          <Link to="/garden">
            <Button size="lg" className="btn-pulse">
              {t('landing.heroCta')}
            </Button>
          </Link>
        </div>
      </section>

      {/* Top Plants Section */}
      <section className="py-16 bg-gradient-to-b from-green-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-800 mb-4">
              {t('landing.topPlantsTitle')}
            </h2>
            <p className="text-gray-600 text-lg">
              {t('landing.topPlantsSubtitle')}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-green-600"></div>
            </div>
          ) : topPlants.length > 0 ? (
            <div className="overflow-x-auto pb-4">
              <div className="flex gap-6 justify-start md:justify-center min-w-max px-4">
                {topPlants.map((plant) => (
                  <div
                    key={plant.id}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300 w-64 flex-shrink-0"
                  >
                    {/* Plant Preview */}
                    <div className="bg-gradient-to-b from-sky-300 to-green-100 h-64 flex items-center justify-center overflow-hidden">
                      {plant.plant_data?.snapshot ? (
                        <img
                          src={plant.plant_data.snapshot}
                          alt={t('landing.cardPreviewAlt', 'Aperçu de jardin partagé')}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <PlantPreview plantData={plant} size={256} />
                      )}
                    </div>

                    {/* Plant Info */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <User size={18} className="text-gray-600" />
                          <span className="font-medium text-gray-800 truncate">
                            {plant.created_by || 'Anonyme'}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Heart size={18} className="fill-current text-red-500" />
                          <span className="font-bold">{plant.likes || 0}</span>
                        </div>
                      </div>

                      <div className="text-sm text-gray-500">
                        <span>
                          {t('landing.stageLabel')} {plant.stage || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500">
              <Sprout size={48} className="mx-auto mb-4 text-gray-400" />
              <p>{t('landing.topPlantsEmpty')}</p>
            </div>
          )}

          <div className="text-center mt-8">
            <Link to="/community">
              <Button variant="outline">
                {t('landing.viewGallery')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-green-800 mb-16">
            {t('landing.howItWorksTitle')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center p-6">
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sprout size={40} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('landing.plantSeedTitle')}</h3>
              <p className="text-gray-600">{t('landing.plantSeedText')}</p>
            </div>

            <div className="text-center p-6">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Droplet size={40} className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('landing.growTitle')}</h3>
              <p className="text-gray-600">{t('landing.growText')}</p>
            </div>

            <div className="text-center p-6">
              <div className="bg-purple-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={40} className="text-purple-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('landing.shareTitle')}</h3>
              <p className="text-gray-600">{t('landing.shareText')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Features */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-green-800 mb-16">
            {t('landing.premiumTitle')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <Sun size={48} className="text-yellow-500 mb-4" />
              <h3 className="text-2xl font-bold mb-2">{t('landing.autoGardenerTitle')}</h3>
              <p className="text-gray-600 mb-4">{t('landing.autoGardenerText')}</p>
              <p className="text-green-600 font-bold">{t('landing.autoGardenerPrice')}</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <ShoppingBag size={48} className="text-green-500 mb-4" />
              <h3 className="text-2xl font-bold mb-2">{t('landing.shopTitle')}</h3>
              <p className="text-gray-600 mb-4">{t('landing.shopText')}</p>
              <p className="text-green-600 font-bold">{t('landing.shopPrice')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-green-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-xl mb-8">
            {t('landing.ctaSubtitle')}
          </p>
          <Link to="/garden">
            <Button size="lg" variant="secondary">
              {t('landing.ctaButton')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default LandingPage
